import { Vector2 } from 'three';

import { SettingModelSettingType } from 'api/models';
import { InputCommand } from 'app/modules/wallforming/components/input-binding/types/inputCommand';
import { ProjectsDialog } from 'app/modules/wallforming/components/projects-manager/types/projectDialog';
import { POIFilterType } from 'app/modules/wallforming/components/scenes/objects/pois/types/poiFilterType';
import { PoiNavigationSource } from 'app/modules/wallforming/components/scenes/objects/pois/types/poiNavigationSource';
import { getNeighboringPoiIndex } from 'app/modules/wallforming/components/scenes/objects/pois/utils/poiUtils';
import { getCanvasPointer } from 'app/modules/wallforming/components/scenes/objects/utils/pointerUtils';
import { cancelPanelQuickReplacementSelection } from 'app/modules/wallforming/components/scenes/tools/quick-panel-replacement/quickPanelReplacementController';
import { InteractionMode } from 'app/modules/wallforming/components/scenes/types/interactionMode';
import { PlacementTab } from 'app/modules/wallforming/components/setting-container/types/placementTab';
import { SettingView } from 'app/modules/wallforming/components/setting-container/types/settingView';

import type { PlacementState } from 'app/modules/wallforming/components/state-management/placement/types';

const pastePointer = new Vector2();

let previousStepperPoiIndex: number | undefined;

export const isDraggingActive = (state: PlacementState) => state.isAutoDragActiveRef.current || state.isDraggingActive();

export const unlessDragging =
    (run: (state: PlacementState) => void): InputCommand =>
    (_, state) => {
        if (isDraggingActive(state)) {
            return false;
        }
        run(state);
    };

export function cancel(state: PlacementState): void {
    if (state.isProjectPanelOpen) {
        if (state.activeProjectDialog !== ProjectsDialog.NONE) {
            state.setActiveProjectDialog(ProjectsDialog.NONE);
        } else {
            state.setIsProjectPanelOpen(false);
        }
    } else if (state.isPanelQuickReplacementOpen) {
        if (!cancelPanelQuickReplacementSelection()) {
            state.setIsPanelQuickReplacementOpen(false);
        }
    } else if (state.inputBindingDialog) {
        state.closeInputBindingDialog();
    } else if (state.isInputBindingPanelOpen) {
        state.setIsInputBindingPanelOpen(false);
    } else if (state.isGroupPanelOpen) {
        state.setIsGroupPanelOpen(false);
    } else if (
        state.interactionMode === InteractionMode.POI_NAVIGATION &&
        state.poiSelection.current.index !== undefined
    ) {
        state.setInteractionMode(InteractionMode.DEFAULT);
    } else if (state.arePivotControlsActive && !state.isTransformationActiveRef.current) {
        state.updatePivotControls();
    } else {
        state.discardArticleInstanceSelection(false);
    }
}

export function paste(state: PlacementState): void {
    const canvas = state.mainWebGLRendererRef.current?.domElement;
    if (canvas && getCanvasPointer(canvas, pastePointer)) {
        state.pasteArticleInstances(pastePointer);
    }
}

export function startTemporaryPoiNavigation(event: Event, state: PlacementState): void {
    event.stopPropagation();
    if (state.isSpaceKeyPressed) {
        return;
    }
    state.setIsSpaceKeyPressed(true);
    state.setInteractionMode(InteractionMode.POI_NAVIGATION);
    if ((state.isDraggingActive() && !state.isAutoDragActiveRef.current) || state.arePivotControlsActive) {
        state.discardArticleInstanceSelection(false);
    }
    if (event.target instanceof HTMLElement) {
        event.target.blur();
    }
}

export const navigatePoi =
    (delta: -1 | 1): InputCommand =>
    (_, state) => {
        if (state.selectedSettingView !== SettingView.AUTOMATED_PLACEMENT) {
            return false;
        }
        const { filterType, index } = state.poiSelection.current;
        const poiIndexes = state.pois.flatMap((poi, poiIndex) => (poi.filterType === filterType ? [poiIndex] : []));
        const position = poiIndexes.indexOf(index ?? -1);
        if (position < 0) {
            return false;
        }
        state.setPoiSelection(
            filterType,
            poiIndexes[(position + delta + poiIndexes.length) % poiIndexes.length],
            PoiNavigationSource.PoiStepper
        );
    };

export const switchPoiType =
    (reversed: boolean): InputCommand =>
    (event, state) => {
        const { filterType, index, source } = state.poiSelection.current;
        const availableTypes = Object.values(POIFilterType).filter((type) =>
            state.pois.some((poi) => poi.filterType === type)
        );
        if (filterType === undefined || availableTypes.length < 2) {
            return false;
        }
        const step = reversed ? -1 : 1;
        const nextType =
            availableTypes[(availableTypes.indexOf(filterType) + step + availableTypes.length) % availableTypes.length];
        const nextIndex = getNeighboringPoiIndex(
            state.pois,
            index,
            nextType,
            source === PoiNavigationSource.PoiStepper ? previousStepperPoiIndex : undefined,
            reversed
        );
        if (nextIndex < 0) {
            return false;
        }
        event.stopPropagation();
        previousStepperPoiIndex = index;
        state.setPoiSelection(nextType, nextIndex, PoiNavigationSource.PoiStepper);
    };

export function navigateCatalogue(state: PlacementState, delta: -1 | 1): boolean {
    const root = document.querySelector(
        state.selectedSettingView === SettingView.CATALOGUE
            ? '[data-setting-catalogue-view]'
            : '[data-automated-catalogue]'
    );
    const rows = Array.from(root?.querySelectorAll<HTMLElement>('[data-catalogue-article-id]') ?? []);
    if (rows.length === 0) {
        return false;
    }
    const index = rows.findIndex((row) => row.dataset.catalogueArticleId === state.selectedArticleId);
    const next = index < 0 ? (delta < 0 ? rows.length - 1 : 0) : (index + delta + rows.length) % rows.length;
    state.setSelectedArticleId(rows[next].dataset.catalogueArticleId ?? null);
    return true;
}

export function navigateSolutions(state: PlacementState, delta: -1 | 1): boolean {
    const count = state.placementSolutions.length;
    if (count === 0) {
        return false;
    }
    const index = state.selectedPlacementSolution ?? (delta < 0 ? 0 : -1);
    state.setSelectedPlacementSolution((index + delta + count) % count);
    return true;
}

export const navigateSettingList =
    (delta: -1 | 1): InputCommand =>
    (event, state) => {
        event.stopPropagation();
        if (event.target instanceof HTMLElement) {
            event.target.blur();
        }
        const isAutomated = state.selectedSettingView === SettingView.AUTOMATED_PLACEMENT;
        if (
            state.selectedSettingView === SettingView.CATALOGUE ||
            (isAutomated && state.automatedPlacementTab === PlacementTab.CATALOGUE)
        ) {
            return navigateCatalogue(state, delta);
        }
        return isAutomated && state.automatedPlacementTab === PlacementTab.SOLUTIONS && navigateSolutions(state, delta);
    };

export const isValidPlacementType = (state: PlacementState, ...types: SettingModelSettingType[]) =>
    state.selectedSettingView === SettingView.AUTOMATED_PLACEMENT && types.includes(state.selectedSettingType);
